/**
 * NAT-2481 — the producer aging table as XLSX: the producers the bucket filter leaves, in the
 * order on screen, and their orders on a second sheet. A position as of today: no period. No
 * request is made — the table already holds every producer the endpoint returned.
 */
import type { IAgingPayableOrder, IAgingProducer } from 'src/types';
import { ymdToDate, type XlsxWorkbookSpec } from 'src/utils/file-downloader';

import { AGING_BUCKET_CONFIG } from '../aging-bucket-config';
import { agingPreamble, sheetOf, type ExportT } from '../position-export';

export function producerAgingWorkbook({
  producers,
  page,
  bucketLabel,
  sortLabel,
  t,
  now = new Date(),
}: {
  /** Already filtered and sorted as on screen. */
  producers: IAgingProducer[];
  /** The page (and section) the table sits on, in the viewer's language. */
  page: string;
  bucketLabel: string | null;
  sortLabel: string;
  t: ExportT;
  now?: Date;
}): XlsxWorkbookSpec {
  const orders = producers.flatMap((producer) =>
    (producer.invoices ?? []).map((order) => ({ producer, order })),
  );
  type OrderRow = { producer: IAgingProducer; order: IAgingPayableOrder };

  return {
    fileName: `${page} - ${t('export_produttori')}`,
    preamble: agingPreamble({ page, bucketLabel, sortLabel, t, now }),
    sheets: [
      sheetOf<IAgingProducer>(
        t('export_produttori'),
        [
          { header: t('produttore'), kind: 'text', cell: (p) => p.producerName },
          { header: t('totale_dovuto'), kind: 'euro', cell: (p) => p.totalOverdue },
          {
            header: t('bucket_peggiore'),
            kind: 'text',
            cell: (p) => t(AGING_BUCKET_CONFIG[p.worstBucket].labelKey),
          },
          { header: t('n_ordini'), kind: 'integer', cell: (p) => p.invoiceCount },
        ],
        producers,
      ),
      sheetOf<OrderRow>(
        t('export_ordini_fornitore'),
        [
          { header: t('produttore'), kind: 'text', cell: ({ producer }) => producer.producerName },
          { header: t('codice_ordine'), kind: 'text', cell: ({ order }) => order.orderCode },
          { header: t('importo'), kind: 'euro', cell: ({ order }) => order.amountDue },
          {
            header: t('data_scadenza'),
            kind: 'date',
            cell: ({ order }) => ymdToDate(order.dueDate),
          },
          { header: t('giorni_ritardo'), kind: 'integer', cell: ({ order }) => order.daysOverdue },
        ],
        orders,
      ),
    ],
  };
}
